import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, History, TrendingUp, TrendingDown, ScanLine, PackageX } from "lucide-react";
import api from "../lib/api";
import { useAutoSync } from "../hooks/useAutoSync";
import { CATEGORIES, CATEGORY_COLORS } from "../lib/constants";
import { money, unitCost } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import FoodDialog from "../components/FoodDialog";
import ReceiptDialog from "../components/ReceiptDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { UNITS } from "../lib/constants";

function PriceHistoryDialog({ food, open, onOpenChange, cur }) {
  if (!food) return null;
  const hist = food.price_history || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="price-history-dialog">
        <DialogHeader><DialogTitle className="font-display">{food.name} — Price History</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {hist.length === 0 && <p className="text-sm text-slate-500">No purchases recorded yet.</p>}
          {hist.map((h, i) => {
            const prev = i > 0 ? hist[i - 1].serving_cost : null;
            let trend = null;
            if (prev != null && h.serving_cost != null) trend = h.serving_cost - prev;
            return (
              <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3">
                <div>
                  <div className="font-mono text-sm font-semibold">{money(h.price, cur)}</div>
                  <div className="text-xs text-slate-500">{h.date} · {h.package_qty} {h.package_unit} {h.store ? `· ${h.store}` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{h.serving_cost != null ? `${money(h.serving_cost, cur)}/serv` : "—"}</div>
                  {trend != null && trend !== 0 && (
                    <div className={`flex items-center justify-end gap-1 text-xs font-semibold ${trend > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {money(Math.abs(trend), cur)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddPurchaseDialog({ food, open, onOpenChange, onSaved, cur }) {
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("oz");
  const [store, setStore] = useState("");
  const [date, setDate] = useState("");
  useEffect(() => {
    if (open && food) {
      setPrice(""); setQty(food.metrics?.package_qty ?? "");
      setUnit(food.package_unit || "oz"); setStore(food.metrics?.store || ""); setDate("");
    }
  }, [open, food]);
  if (!food) return null;
  const save = async () => {
    if (!price) { toast.error("Enter a price"); return; }
    try {
      await api.post(`/foods/${food.id}/purchases`, {
        price: parseFloat(price), package_qty: qty ? parseFloat(qty) : null,
        package_unit: unit, store, date: date || null,
      });
      toast.success("New price logged");
      onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Could not save purchase"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-purchase-dialog">
        <DialogHeader><DialogTitle className="font-display">Log New Price — {food.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Old prices are kept. This becomes the active price.</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Price</Label><Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid="purchase-price-input" /></div>
          <div><Label>Amount</Label><Input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Unit</Label>
            <Select value={unit} onValueChange={setUnit}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Store</Label><Input value={store} onChange={(e) => setStore(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="purchase-save-button" className="w-full rounded-full bg-brand hover:bg-brand-hover">Log price</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Foods() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [foods, setFoods] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [histFood, setHistFood] = useState(null);
  const [purchaseFood, setPurchaseFood] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/foods");
    setFoods(data);
  }, []);
  useAutoSync(load, [load]);

  const del = async (id) => {
    await api.delete(`/foods/${id}`);
    toast.success("Food deleted");
    load();
  };

  const filtered = foods.filter((f) =>
    (cat === "All" || f.category === cat) &&
    f.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Food Library</h1>
          <p className="text-sm text-slate-500">{foods.length} items · enter each grocery once</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReceiptOpen(true)} data-testid="add-from-receipt-button"
            className="rounded-full border-slate-200"><ScanLine className="mr-1.5 h-4 w-4" />Receipt</Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-food-button"
            className="rounded-full bg-brand px-5 hover:bg-brand-hover"><Plus className="mr-1.5 h-4 w-4" />Add Food</Button>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="food-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search foods…" className="rounded-full pl-9" />
        </div>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-filter-${c}`}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              cat === c ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((f) => {
          const m = f.metrics || {};
          return (
            <div key={f.id} data-testid={`food-card-${f.id}`}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:border-brand/40 hover:shadow-lg hover:shadow-brand/5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate font-display font-bold">{f.name}</h3>
                  {f.brand && <p className="truncate text-xs text-slate-400">{f.brand}</p>}
                </div>
                <Badge className={`shrink-0 border-0 ${CATEGORY_COLORS[f.category] || CATEGORY_COLORS.Other}`}>{f.category}</Badge>
              </div>

              {f.needs_package_size && (
                <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-amber-600">
                  <PackageX className="h-3.5 w-3.5" />Needs package size
                </div>
              )}

              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">My serving</p>
                  <p className="font-mono text-2xl font-bold text-brand" data-testid={`food-serving-cost-${f.id}`}>
                    {m.serving_cost != null ? money(m.serving_cost, cur) : "—"}
                  </p>
                  <p className="text-xs text-slate-500">{f.serving_amount ? `${f.serving_amount} ${f.serving_unit}` : "set serving"}</p>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p className="font-mono">{money(m.price, cur)} / {m.package_qty} {m.package_unit}</p>
                  {m.cost_per_oz != null && <p className="font-mono">{unitCost(m.cost_per_oz, cur)}/oz</p>}
                  {m.cost_per_ml != null && <p className="font-mono">{unitCost(m.cost_per_ml, cur, 4)}/ml</p>}
                  {m.cost_per_item != null && <p className="font-mono">{unitCost(m.cost_per_item, cur)}/item</p>}
                  {m.cost_per_mfg_serving != null && <p className="font-mono">{unitCost(m.cost_per_mfg_serving, cur)}/mfg</p>}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-1 border-t border-slate-100 pt-3">
                <button onClick={() => { setEditing(f); setDialogOpen(true); }} data-testid={`edit-food-${f.id}`}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" />Edit</button>
                <button onClick={() => setHistFood(f)} data-testid={`history-food-${f.id}`}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><History className="h-3.5 w-3.5" />History</button>
                <button onClick={() => setPurchaseFood(f)} data-testid={`repurchase-food-${f.id}`}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Plus className="h-3.5 w-3.5" />Price</button>
                <button onClick={() => del(f.id)} data-testid={`delete-food-${f.id}`}
                  className="ml-auto rounded-lg px-2 py-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="mt-16 text-center text-slate-400">
          <p className="text-sm">No foods found. Tap <span className="font-semibold text-brand">Add Food</span> to start.</p>
        </div>
      )}

      <FoodDialog open={dialogOpen} onOpenChange={setDialogOpen} food={editing} onSaved={load} />
      <PriceHistoryDialog food={histFood} open={!!histFood} onOpenChange={(v) => !v && setHistFood(null)} cur={cur} />
      <AddPurchaseDialog food={purchaseFood} open={!!purchaseFood} onOpenChange={(v) => !v && setPurchaseFood(null)} onSaved={load} cur={cur} />
      <ReceiptDialog open={receiptOpen} onOpenChange={setReceiptOpen} onSaved={load} />
    </div>
  );
}
